import { TKioskSelectionFeed } from 'types/kiosk/TKioskSelectionFeed';

const EMPTY_FEED: TKioskSelectionFeed = { active: [], planned: [], allEmployees: [] };

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const asRecords = (rows: unknown[]) =>
  rows.filter((r): r is Record<string, unknown> => typeof r === 'object' && r !== null);

/** Every variant requires a truthy employeeId and a non-empty fullName string. */
const hasRequiredIdentity = (row: Record<string, unknown>) =>
  Boolean(row.employeeId) && typeof row.fullName === 'string' && row.fullName.length > 0;

const toActive = (rows: unknown[]) =>
  asRecords(rows)
    .filter(hasRequiredIdentity)
    .map(r => ({
      employeeId: Number(r.employeeId),
      registrationId: r.registrationId ? Number(r.registrationId) : 0,
      fullName: String(r.fullName),
      startTimeUtc: String(r.startTimeUtc ?? ''),
      expectedClockOutUtc: r.expectedClockOutUtc ? String(r.expectedClockOutUtc) : null,
      hasPin: Boolean(r.hasPin),
    }));

const toPlanned = (rows: unknown[]) =>
  asRecords(rows)
    .filter(hasRequiredIdentity)
    .map(r => ({
      employeeId: Number(r.employeeId),
      fullName: String(r.fullName),
      contractDateId: r.contractDateId ? Number(r.contractDateId) : null,
      plannedStartUtc: String(r.plannedStartUtc ?? ''),
      plannedEndUtc: String(r.plannedEndUtc ?? ''),
      hasPin: Boolean(r.hasPin),
    }));

const toEmployees = (rows: unknown[]) =>
  asRecords(rows)
    .filter(hasRequiredIdentity)
    .map(r => ({ employeeId: Number(r.employeeId), fullName: String(r.fullName), hasPin: Boolean(r.hasPin) }));

/**
 * Maps the raw selection-feed body (treated as unknown) to typed lists, mirroring mobile's validation:
 * drop rows missing employeeId/fullName; registrationId defaults to 0; expectedClockOutUtc '' -> null;
 * a malformed/empty body returns three empty arrays.
 */
export const fromResponseToKioskSelectionFeed = (response: unknown): TKioskSelectionFeed => {
  if (typeof response !== 'object' || response === null) {
    return EMPTY_FEED;
  }
  const body = response as Record<string, unknown>;
  return {
    active: toActive(asArray(body.active)),
    planned: toPlanned(asArray(body.planned)),
    allEmployees: toEmployees(asArray(body.allEmployees)),
  };
};
